import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { HrAdminApprovalFields } from '../../components/HrAdminApprovalFields';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { RequestCancelActions } from '../../components/RequestCancelActions';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getTimePermissionModuleCapabilities,
  TIME_PERMISSION_ROUTE_CAPABILITIES,
} from '../../config/timePermissionCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  approveTimePermissionRequest,
  cancelTimePermissionRequest,
  getTimePermissionRequestDetails,
  rejectTimePermissionRequest,
} from '../../services/timePermissionService';
import { getWorkflowDetails } from '../../services/workflowService';
import type { TimePermissionRequestDetails } from '../../types/timePermission';
import type { WorkflowDetails } from '../../types/workflow';
import { isPermissionApprovalType } from '../../utils/hrAdminApprovalFields';
import { workflowErrorMessage } from '../../utils/workflowErrors';
import { confirmAction } from '../../utils/confirmAction';
import { timePermissionErrorMessage } from '../../utils/timePermissionErrors';
import { canShowApprovedRequestCancel } from '../../utils/requestCancelWindow';
import { isPendingWorkflowActionStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'TimePermissionApproval'
>;

function mergePermissionRequestDetails(
  workflow: WorkflowDetails,
  permission: TimePermissionRequestDetails,
): WorkflowDetails {
  const owner = permission.employee;
  const ownerName = owner?.displayName?.trim() || null;
  const ownerNumber = owner?.employeeNumber?.trim() || null;
  const ownerDesignation = owner?.designationName?.trim() || null;
  return {
    ...workflow,
    employee: {
      employeeId: owner?.employeeId ?? workflow.employee.employeeId,
      employeeNumber: ownerNumber || workflow.employee.employeeNumber,
      displayName: ownerName || workflow.employee.displayName,
      departmentName: workflow.employee.departmentName,
      designationName:
        ownerDesignation || workflow.employee.designationName,
    },
    requestDetails: {
      ...(workflow.requestDetails ?? {}),
      permissionDate:
        permission.permissionDate ?? workflow.requestDetails?.permissionDate,
      fromTime: permission.fromTime ?? workflow.requestDetails?.fromTime,
      toTime: permission.toTime ?? workflow.requestDetails?.toTime,
      durationMinutes:
        permission.durationMinutes ?? workflow.requestDetails?.durationMinutes,
      permissionTypeName:
        permission.permissionType.permissionTypeName ??
        permission.permissionType.permissionTypeCode ??
        workflow.requestDetails?.permissionTypeName,
      permissionType:
        permission.permissionType.permissionTypeCode ??
        workflow.requestDetails?.permissionType,
      reason: permission.reason ?? workflow.requestDetails?.reason,
    },
    requestReferenceId:
      permission.permissionRequestId ?? workflow.requestReferenceId,
    rejectReason: permission.rejectReason ?? workflow.rejectReason,
    cancellationReason:
      permission.cancellationReason ?? workflow.cancellationReason,
    canCancel: permission.canCancel,
  };
}

export function TimePermissionApprovalScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getTimePermissionModuleCapabilities(auth.permissions);
  const workflowInstanceId = route.params.workflowInstanceId;

  const [details, setDetails] = useState<WorkflowDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const canAct =
    TIME_PERMISSION_ROUTE_CAPABILITIES.approveAction &&
    TIME_PERMISSION_ROUTE_CAPABILITIES.rejectAction &&
    isPendingWorkflowActionStatus(details?.workflowStatusCode);
  const permissionRequestId = details?.requestReferenceId ?? null;

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canAccessPermissionApproval) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
        setDetails(null);
        setError('Permission request was not found.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const next = await getWorkflowDetails(workflowInstanceId);
        if (
          next.requestTypeCode &&
          !isPermissionApprovalType(next.requestTypeCode)
        ) {
          if (!isRefresh) setDetails(null);
          setError('This approval is not a permission request.');
          return;
        }

        let resolved = next;
        if (next.requestReferenceId != null) {
          try {
            const permission = await getTimePermissionRequestDetails(
              next.requestReferenceId,
            );
            resolved = mergePermissionRequestDetails(next, permission);
          } catch {
            resolved = next;
          }
        }
        setDetails(resolved);
        setError(null);
      } catch (caught) {
        if (!isRefresh) setDetails(null);
        setError(
          workflowErrorMessage(
            caught,
            'Unable to load permission approval details.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canAccessPermissionApproval, workflowInstanceId],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const handleApprove = () => {
    if (!canAct || acting || permissionRequestId == null) return;
    confirmAction({
      title: 'Approve permission',
      message: 'Approve this permission request?',
      confirmText: 'Approve',
      onConfirm: () => {
        setActing(true);
        setActionError(null);
        return approveTimePermissionRequest(permissionRequestId)
          .then(() => navigation.goBack())
          .catch(caught => {
            setActionError(
              timePermissionErrorMessage(
                caught,
                'Unable to approve permission request.',
              ),
            );
          })
          .finally(() => setActing(false));
      },
    });
  };

  const handleReject = async () => {
    if (!canAct || acting || permissionRequestId == null) return;
    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      setActionError('Rejection reason is required.');
      return;
    }
    setActing(true);
    setActionError(null);
    try {
      await rejectTimePermissionRequest(permissionRequestId, trimmedReason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        timePermissionErrorMessage(
          caught,
          'Unable to reject permission request.',
        ),
      );
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (acting || permissionRequestId == null) return;
    setActing(true);
    setActionError(null);
    try {
      await cancelTimePermissionRequest(permissionRequestId, reason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        timePermissionErrorMessage(
          caught,
          'Unable to cancel permission request.',
        ),
      );
    } finally {
      setActing(false);
    }
  };

  const employeeName =
    details?.employee.displayName ||
    details?.employee.employeeNumber ||
    'Employee unavailable';

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
        textAlign: 'center' as const,
      },
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.PERMISSION_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Permission request detail"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading permission approval" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Permission request detail"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                load(true).catch(() => undefined);
              }}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader title={employeeName} onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.profileRow}>
                <ProfileAvatar name={employeeName} size={56} />
                <View style={styles.profileText}>
                  <Text style={themed.name}>{employeeName}</Text>
                  {details.employee.employeeNumber ? (
                    <Text style={themed.meta}>
                      {details.employee.employeeNumber}
                    </Text>
                  ) : null}
                  {details.employee.designationName ? (
                    <Text style={themed.meta}>
                      {details.employee.designationName}
                    </Text>
                  ) : null}
                </View>
                <LeaveStatusBadge
                  label={details.workflowStatusLabel}
                  statusCode={details.workflowStatusCode}
                />
              </View>

              <HrAdminApprovalFields details={details} palette={palette} />
            </AppCard>

            {actionError ? (
              <Text accessibilityRole="alert" style={themed.actionError}>
                {actionError}
              </Text>
            ) : null}

            {canAct && rejectMode ? (
              <View style={styles.rejectBlock}>
                <AppInput
                  label="Rejection reason"
                  value={rejectReason}
                  onChangeText={value => {
                    setRejectReason(value);
                    setActionError(null);
                  }}
                  placeholder="Enter reason for rejection"
                />
                <AppButton
                  title="Confirm Reject"
                  variant="danger"
                  onPress={handleReject}
                  loading={acting}
                  disabled={acting}
                />
                <AppButton
                  title="Cancel"
                  variant="text"
                  onPress={() => {
                    setRejectMode(false);
                    setRejectReason('');
                    setActionError(null);
                  }}
                  disabled={acting}
                />
              </View>
            ) : canAct ? (
              <View style={styles.actions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Reject"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={() => {
                    setRejectMode(true);
                    setActionError(null);
                  }}
                  style={[
                    styles.actionButton,
                    styles.rejectButton,
                    {
                      borderColor: palette.error,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.rejectLabel, { color: palette.error }]}>
                    Reject
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Approve"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={handleApprove}
                  style={[
                    styles.actionButton,
                    {
                      backgroundColor: palette.success,
                      borderColor: palette.success,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.approveLabel, { color: palette.onSuccess }]}>
                    Approve
                  </Text>
                </Pressable>
              </View>
            ) : null}

            <RequestCancelActions
              visible={canShowApprovedRequestCancel({
                capabilityCanCancel: capabilities.canCancel,
                canAct,
                statusCode: details.workflowStatusCode,
                fromDate:
                  typeof details.requestDetails?.permissionDate === 'string'
                    ? details.requestDetails.permissionDate
                    : typeof details.requestDetails?.fromDate === 'string'
                      ? details.requestDetails.fromDate
                      : null,
                toDate:
                  typeof details.requestDetails?.permissionDate === 'string'
                    ? details.requestDetails.permissionDate
                    : typeof details.requestDetails?.toDate === 'string'
                      ? details.requestDetails.toDate
                      : null,
              })}
              palette={palette}
              startTitle="Cancel Permission"
              confirmTitle="Confirm Cancel"
              keepTitle="Keep Permission"
              busy={acting}
              error={actionError}
              onConfirm={handleCancel}
            />
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
    minWidth: 0,
  },
  profileText: { flex: 1, gap: 2, minWidth: 0 },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  rejectBlock: {
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  actionButton: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
    maxWidth: '100%',
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectButton: {
    backgroundColor: 'transparent',
  },
  rejectLabel: {
    ...typography.button,
    fontWeight: '700',
  },
  approveLabel: {
    ...typography.button,
    fontWeight: '700',
  },
});
